import { useEffect, useId, useRef } from "react";
import { clientLogger } from "~/logger.client";
import type { RouterOutputs } from "~/services/offstreetApi/types";
import { ACCEPT_UI_SCRIPT_URLS } from "../../paymentSdkUrls";
import { useScript } from "../useScript";

type AcceptUIResponse = {
	messages?: {
		resultCode?: string;
		message?: Array<{ code?: string; text?: string }>;
	};
	opaqueData?: {
		dataDescriptor?: string;
		dataValue?: string;
	};
	encryptedCardData?: {
		cardNumber?: string;
		expDate?: string;
		bin?: string;
	};
};

type AuthorizeNetCallbacks = {
	onApproval: (nonce: string) => void;
	onError: (message: string) => void;
};

type UseAuthorizeNetProps = {
	paymentSession?: RouterOutputs["paymentSession"]["createPaymentSessionPublic"]["session"];
	callbacks: AuthorizeNetCallbacks;
};

// AcceptUI looks up the responseHandler by string name on the global window object.
// We use a per-instance unique handler name to avoid collisions if multiple instances mount.
type AcceptUIResponseHandler = (response: AcceptUIResponse) => void;

declare global {
	interface Window {
		[key: `acceptUIResponseHandler_${string}`]: AcceptUIResponseHandler | undefined;
	}
}

export const useAuthorizeNet = ({ paymentSession, callbacks }: UseAuthorizeNetProps) => {
	const callbacksRef = useRef<AuthorizeNetCallbacks>(callbacks);
	callbacksRef.current = callbacks;

	// Dedup guard: AcceptUI nonces are single-use, so the same nonce should never be processed twice
	const lastProcessedNonce = useRef<string | null>(null);
	const rawId = useId();
	const handlerSuffix = rawId.replace(/[^a-zA-Z0-9]/g, "");
	const responseHandlerName = `acceptUIResponseHandler_${handlerSuffix}` as const;

	const isLive = paymentSession?.client?.isLive ?? false;
	const scriptUrl = isLive ? ACCEPT_UI_SCRIPT_URLS.live : ACCEPT_UI_SCRIPT_URLS.sandbox;

	const isReady = useScript(scriptUrl, {
		enabled: !!paymentSession,
		logGroup: "AUTHORIZE_NET_SCRIPT_LOAD_ERROR",
	});

	useEffect(() => {
		const handler: AcceptUIResponseHandler = (response) => {
			if (response.messages?.resultCode !== "Ok") {
				const errorText = response.messages?.message?.[0]?.text ?? "Payment failed";
				callbacksRef.current.onError(errorText);
				return;
			}

			const nonce = response.opaqueData?.dataValue;
			if (!nonce) {
				clientLogger.error(
					"AcceptUI response missing opaqueData.dataValue",
					new Error("Missing payment nonce"),
					{ logGroup: "AUTHORIZE_NET_MISSING_NONCE" },
				);
				callbacksRef.current.onError("Payment failed");
				return;
			}

			if (lastProcessedNonce.current === nonce) {
				return;
			}
			lastProcessedNonce.current = nonce;

			callbacksRef.current.onApproval(nonce);
		};

		window[responseHandlerName] = handler;

		return () => {
			window[responseHandlerName] = undefined;
		};
	}, [responseHandlerName]);

	return { isReady, responseHandlerName };
};
